import React, { useEffect, useState, useMemo } from "react";
import LanguageService from "../api/language";
import { useSelector } from "react-redux";
import TemplateTable from "../common/Layout/templatePages/templateTable";
import Breadcrumbs from "../common/breadcrumbs";
import { useTranslation } from "react-i18next";
import { Box, Divider, Typography, Tabs, Tab, Button } from "@mui/material";
import { useTheme, styled } from "@mui/material/styles";
import handleNavigate from "../common/customFunctions/handleNavigate";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import Loading from "../common/loading";

const StyledButton = styled((props) => <Button {...props} />)(({ theme }) => ({
  height: "40px",
  backgroundColor: theme.palette.custom.buttonColor,
  color: theme.palette.custom.buttonTextColor,
  "&:hover": {
    backgroundColor: theme.palette.custom.buttonHoverColor,
    color: theme.palette.custom.buttonHoverTextColor,
    boxShadow: "none",
  },
}));

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}
CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};
function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const StyledTab = styled((props) => <Tab disableRipple {...props} />)(
  ({ theme }) => ({
    textTransform: "none",
    fontWeight: theme.typography.fontWeightRegular,
    fontSize: theme.typography.pxToRem(15),
    backgroundColor: theme.palette.custom.backgroundColor,
    borderRadius: "3px",
    padding: "8px 16px",
    color: theme.palette.custom.text,
    height: "36px",
    minHeight: "36px",
    "&.Mui-selected": {
      color: theme.palette.custom.text,
    },
    "&.Mui-focusVisible": {
      backgroundColor: "rgba(100, 95, 228, 0.32)",
      color: theme.palette.primary,
    },
    "&:hover": {
      backgroundColor: theme.palette.custom.buttonColor,
      color: theme.palette.custom.buttonHoverTextColor,
      opacity: "70%",
    },
  })
);

export default function LanguageApi() {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const organziation = useSelector(({ organziation }) => organziation);
  const [languageProfiles, setLanguageProfiles] = useState([]);
  const [translations, setTranslations] = useState([]);
  const [loadingTranslations, setLoadingTranslations] = useState(true);
  const [loadingValue, setLoadingValue] = useState(true);
  const [value, setValue] = useState(0);
  const breadcrumbs = [
    { id: 0, name: "Home", href: "./" },
    { id: 1, name: "Languages", href: "languageApi" },
  ];
  const translationHeadCells = useMemo(() => {
    const langCols = languageProfiles.map((lang) => ({
      id: lang.code,
      label: (
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <img
            src={`${window.env.REACT_APP_PUBLIC_URL}/images/flags/${lang.code}.png`}
            alt={lang.name}
            width="20"
          />
          {lang.name}
        </Box>
      ),
    }));

    return [
      ...langCols,
      { id: "variable_name", label: t("Variable Name") },
      { id: "sensitive", label: t("Sensitive") },
      { id: "action", buttons: ["edit"] },
    ];
  }, [languageProfiles, t]);

  const translationTableRows = useMemo(() => {
    return translations.map((entry) => {
      const row = {
        language_dictionary_id: entry.language_dictionary_id,
        variable_name: entry.variable_name,
        sensitive: entry.sensitive.toString(),
      };

      entry.translation.forEach((t) => {
        row[t.code] = t.value;
      });

      return row;
    });
  }, [translations]);

  useEffect(() => {
    if (value !== 0) return;
    const orgObj =
      organziation ?? JSON.parse(localStorage.getItem("defaultOrganization"));
    const apiKey = orgObj?.api_key_list?.[0]?.key;
    if (!apiKey) return;
    setLoadingValue(true);
    setLoadingTranslations(true);
    LanguageService.GetLanguageProfile("", "", "", "active", apiKey).then(
      (profileRes) => {
        console.log(profileRes);
        setLanguageProfiles(profileRes.language_profile);
        setLoadingValue(false);
      }
    );
    LanguageService.GetLanguageDictionary("", "active", apiKey).then(
      (dictionaryRes) => {
        console.log(dictionaryRes);
        setTranslations(dictionaryRes.language_dictionary);
        setLoadingTranslations(false);
      }
    );
  }, [value, organziation]);

  const handleButton = (event, buttonType, row) => {
    if (buttonType === "view") {
      handleView(event, row);
    }
    if (buttonType === "edit") {
      handleEditTranslation(event, row);
    }
  };
  const handleView = (event, row) => {
    event.stopPropagation();
    handleNavigate(event, navigate, `/addEditLanguage?id=${row.language_id}`);
  };

  const handleEditTranslation = (event, row) => {
    event.stopPropagation();
    handleNavigate(
      event,
      navigate,
      `/addEditTranslations?language_dictionary_id=${row.language_dictionary_id}`
    );
  };

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };
  return (
    <Box
      sx={{
        p: "12px",
        backgroundColor: theme.palette.custom.backgroundColor,
      }}
    >
      <Breadcrumbs breadcrumbs={breadcrumbs} />
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "block", sm: "center" },
          gap: "1rem",
          mb: "1rem",
          flexWrap: "wrap"
        }}
      >
        <Typography
          sx={{ fontSize: "45px", mb: 0, color: theme.palette.custom.text }}
          variant="h2"
          gutterBottom
        >
          {t("Language API")}
        </Typography>
        <Box
          sx={{
            display: "flex",
            gap: "1rem",
            flexWrap: "wrap"
          }}
        >
          <StyledButton
            onClick={() => handleNavigate(event, navigate, "/addEditLanguage")}
          >
            {t("Add Language")}
          </StyledButton>
          <StyledButton
            onClick={() =>
              handleNavigate(event, navigate, "/addEditTranslations")
            }
          >
            {t("Add Translation")}
          </StyledButton>
        </Box>
      </Box>
      <Divider
        sx={{ mb: "10px", backgroundColor: theme.palette.custom.borderColor }}
      />
      <Box>
        <Tabs
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          value={value}
          TabIndicatorProps={{
            style: { backgroundColor: theme.palette.custom.borderColor },
          }}
          onChange={handleChange}
          aria-label="basic tabs example"
        >
          <StyledTab label={t("Languages")} {...a11yProps(0)} />
          <StyledTab label={t("Translations")} {...a11yProps(1)} />
        </Tabs>
      </Box>

      <CustomTabPanel value={value} index={0}>
        {loadingValue ? (
          <Box sx={{ height: "30vh", pt: "10%" }}>
            <Loading />
          </Box>
        ) : (
          <TemplateTable
            headCells={[
              { id: "name" },
              { id: "code", label: "code" },
              { id: "status", label: "status" },
              { id: "action", buttons: ["view"] },
            ]}
            handleButton={handleButton}
            itemsList={languageProfiles}
            itemId="language_id"
            loading={loadingValue}
            searchAttributes={["name", "code"]}
            allowExport
            exportTitle={t("LanguageProfiles")}
            showIcon={true}
          />
        )}
      </CustomTabPanel>
      <CustomTabPanel value={value} index={1}>
        {loadingTranslations ? (
          <Box sx={{ height: "30vh", pt: "10%" }}>
            <Loading />
          </Box>
        ) : (
          <TemplateTable
            headCells={translationHeadCells}
            handleButton={handleButton}
            itemsList={translationTableRows}
            itemId="language_dictionary_id"
            loading={loadingTranslations}
            searchAttributes={[
              "variable_name",
              "sensitive",
              ...languageProfiles.map((p) => p.code),
            ]}
            allowExport
            exportTitle={t("Language Translations")}
          />
        )}
      </CustomTabPanel>
    </Box>
  );
}
